import { useEffect, useRef } from "react";

import { hasProcessingDbTags } from "../../../providers/TTSFileStatusProvider";
import { getItemQuery } from "../../../stores/item";
import { getPassageQuery } from "../../../stores/passage";

const TERMINAL_REQUEST_STATUSES = new Set(["GENERATED", "FAILED", "STUCK"]);

const refreshFormTtsTagStatuses =
  (client, setValue, lastTerminalRef, ttsTagStatusMap) =>
  async (id, query, dataKey, formKey) => {
    if (!id) return;
    const status = ttsTagStatusMap[id]?.["__requestStatus"]?.status;

    // No status (map cleared) or a new run (PROCESSING/etc.): drop the
    // remembered terminal so the next terminal triggers a refresh. STUCK
    // is treated as terminal — refetch to reconcile DB.
    if (!TERMINAL_REQUEST_STATUSES.has(status)) {
      delete lastTerminalRef.current[id];
      return;
    }
    if (lastTerminalRef.current[id] === status) return;
    lastTerminalRef.current[id] = status;

    try {
      const result = await client.query(query, { id }).toPromise();
      if (result.error) {
        console.error("[TTS] ttsTagStatuses refresh failed", result.error);
        return;
      }
      const tagStatuses = result.data?.[dataKey]?.ttsTagStatuses;
      if (Array.isArray(tagStatuses)) {
        setValue(`${formKey}.ttsTagStatuses`, tagStatuses, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    } catch (err) {
      console.error("[TTS] ttsTagStatuses refresh threw", err);
    }
  };

// Returns true when an object has TTS enabled but no status entries yet —
// the signature of a clone+ttsGenerate=true where CCR queues generation
// asynchronously and hasn't written any ttsTagStatuses rows yet.
const hasMissingTtsStatuses = (obj) =>
  !!obj?.useTextToSpeech && (obj?.ttsTagStatuses ?? []).length === 0;

// Encapsulates two TTS effects on Workspace:
//   1. Auto-subscribe when the loaded item/passage already has DB tags in a
//      processing state (page refresh mid-generation), or when TTS is enabled
//      but no status entries exist yet (clone+ttsGenerate=true race).
//   2. When the WebSocket reports a terminal __requestStatus, refresh just
//      the form's `ttsTagStatuses` field via a direct GraphQL query +
//      setValue (avoiding the store path which would reset the entire form).
const useTtsStatusReconciler = ({
  client,
  user,
  item,
  passage,
  itemId,
  passageId,
  itemStore,
  passageStore,
  programs,
  users,
  startSubscription,
  ttsTagStatusMap,
  setValue,
  stopSubscriptionRef,
}) => {
  const lastTtsTerminalRef = useRef({});

  // Auto-subscribe to TTS status updates if tags are still processing when
  // the page loads. Refresh the stores so the form picks up current DB state.
  useEffect(() => {
    if (!user?.id || !client) return;

    const passageDbTags = passage?.ttsTagStatuses ?? [];
    const itemDbTags = item?.ttsTagStatuses ?? [];
    const passageHasProcessing = hasProcessingDbTags(passageDbTags, {});
    const itemHasProcessing = hasProcessingDbTags(itemDbTags, {});
    const itemHasMissingStatuses = hasMissingTtsStatuses(item);
    const passageHasMissingStatuses = hasMissingTtsStatuses(passage);

    if (
      !passageHasProcessing &&
      !itemHasProcessing &&
      !itemHasMissingStatuses &&
      !passageHasMissingStatuses
    )
      return;

    const requestIds = [item?.id, passage?.id].filter(Boolean);
    if (!requestIds.length) return;

    startSubscription(client)(user.id)(requestIds).then((unsub) => {
      stopSubscriptionRef.current = unsub;
    });

    if (programs?.length && users) {
      if ((passageHasProcessing || passageHasMissingStatuses) && passageId)
        passageStore.fetch(users)(programs)(passageId);
      if ((itemHasProcessing || itemHasMissingStatuses) && itemId)
        itemStore.fetch(users)(programs)(itemId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passage?.id, item?.id]);

  // Refresh form ttsTagStatuses when the WebSocket reports a terminal
  // __requestStatus for either object.
  useEffect(() => {
    const refresh = refreshFormTtsTagStatuses(
      client,
      setValue,
      lastTtsTerminalRef,
      ttsTagStatusMap,
    );
    refresh(passageId, getPassageQuery, "passageByID", "passage");
    refresh(itemId, getItemQuery, "itemByID", "item");
  }, [ttsTagStatusMap, passageId, itemId, client, setValue]);
};

export { useTtsStatusReconciler };
